import { StandardAlert } from '@brandfolder/react';
import { sanitize } from '@brandfolder/utilities';
import { t, Trans } from '@lingui/macro';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import {
  add as addFilter,
  remove as removeFilter,
} from '@api/search-filters';
import getSearchableThings from '@api/searchable-things';
import getResourceAssets, { getSectionAssetCounts } from '@api/v4/resources/assets';
import { getCustomFieldKeys } from '@api/v4/resources/custom_fields';
import {
  formatLabelsFlat,
  formatLabelsTree,
  get as getLabels,
} from '@api/v4/resources/labels';
import AdvancedDownloadModal from '@components/advanced_download/main';
import { determineUGTLocale } from '@components/asset/modal/tabs/edit/helpers';
import { flattenCustomFieldKeysList } from '@components/asset/modal/tabs/edit/helpers';
import FetchControllersWrapper from '@components/common/fetch_controllers_wrapper';
import { ProcessingLoader } from '@components/common/loader/main';
import { DownloadAlertDialog } from '@components/download_alert/DownloadAlertDialog';
import ApproveRequestActions from '@components/request_download/approve_request_actions';
import { CustomFieldsEvents } from '@components/settings/brandfolder/custom-fields-tab/CustomFieldsFormEnums';
import InternalShareBar from '@components/show_page/other/internal_share_bar';
import { fetchCanvaCredentials } from '@components/show_page/sections/asset/canva/helper';
import { fetchShareManifest, shareManifestExpired } from '@components/show_page/sections/helpers';
import SearchDetails from '@components/show_page/sections/search_details/SearchDetails';
import filterLogic from '@components/show_page/sections/section_search/filterLogic';
import { resolvePeopleSearchExecuteSearchType } from '@components/show_page/sections/section_search/peopleSearchExecuteAnalytics';
import { FilterType, SearchOperatorOptions } from '@components/show_page/sections/section_search/SearchTypes';
import { resolvePeopleTaggingHostProductArea } from '@components/bulk_management/people_management/peopleHubAmplitude';
import {
  getDefaultViewOptions,
  getSortOption,
  setUserViewOptions,
  SortOptions,
  updateUserViewOptionsInLocalStorage,
} from '@components/show_page/sections/view_options/helper';
import ViewOptions from '@components/show_page/sections/view_options/ViewOptions';
import Web2PrintModal from '@components/web_2_print/web_2_print_modal';
import { manageFilters, defaultFilters } from '@helpers/filters';
import { getAiSearch, isAiSearchOn, semanticSearchFeature, userNlsEnabled } from '@helpers/semantic_search';
import { defaultSearchFilterOperators, Brandfolder, Collection } from '@helpers/show_page_helpers';
import { getStorage, setStorage, StorageTypes } from '@helpers/storage';
import { amplitudeService } from '../../../services/amplitude';

import BulkSelectBar from "../bulk_actions/BulkSelectBar";

import { QUERY_REFINEMENT_WINDOW_SECONDS } from './section_search/searchConfig';
import CustomDragLayer from './asset/custom_drag_layer';
import FiltersDrawer from "./filters_drawer/main";
import LabelsBreadcrumbs from "./labels_breadcrumbs/main";
import PinnedSearches from "./pinned_searches/main";
import Section from './section';
import SectionSearch from "./section_search/main";
import SectionSort from "./section_sort/SectionSort";
import SemanticNoResults from './semantic_no_results/SemanticNoResults';
import SectionsContext from './sectionsContext';

import './styles/_search_and_view_details.scss';
import './styles/show_page.scss';

class ShowPage extends React.Component {
  constructor(props) {
    super(props);

    const {
      defaultSortOrder,
      paginatePer,
      searchPageMode: isSearchPage,
    } = this.props;

    this.state = {
      activeLabelKey: null,
      activeSection: 'all',
      assets: {},
      assetsProcessing: 0,
      canvaApi: null,
      customFieldKeys: null,
      dependentCustomFields: null,
      downloadAlertCallback: undefined,
      entireSectionSelected: new Set(),
      filters: { ...defaultFilters() },
      filtersDrawerOpen: 'none', // 'none', 'labels', 'advanced-filters'
      filterStrings: [],
      internalShareLinkExpired: false,
      labels: {},
      lastSelected: null,
      lastVisited: {},
      loading: true,
      // Persisted after people deep-link (sessionStorage); needed so pagination keeps sending identity_ids
      peopleSearchIdentityId: null,
      searchableThings: null,
      searchFilters: this.props.searchFilters,
      lastSearchTimestamp: null,
      searchKey: 'none',
      searchFilterOperators: this.initialSearchFilterOperators(),
      searchQuery: this.initialSearchQuery(),
      searchResultsAnnouncement: '',
      searchSubmitted: !!this.initialSearchQuery(),
      sections: {},
      selectedAssetKeys: new Set(),
      selectedViewOnlyAssetKeys: new Set(),
      vsAttachmentIds: {},
      nlsScores: {},
      nlsEsQuery: null,
      showAdvancedDownloadModal: false,
      showDownloadAlert: false,
      showWebToPrintModal: false,
      tasks: {},
      searchPageMode: isSearchPage,
      userViewOptions: {
        ...setUserViewOptions(defaultSortOrder, paginatePer, this.initialSearchOperator()),
        ...(isSearchPage ? { showSections: false } : {}),
        ...(isSearchPage && semanticSearchFeature() && userNlsEnabled() && getAiSearch().enabled ? { sortOption: SortOptions.Relevancy } : {}),
        ...(isAiSearchOn() && !this.initialSearchOperator() ? {
          searchOperator: SearchOperatorOptions[BFG?.semanticSearchSettings?.default_semantic_search_operator]
            || SearchOperatorOptions.Strict,
        } : {}),
      },
      windowDimensions: {}, // updates via 'resize' event listener
      aiSearchEnabled: userNlsEnabled() && getAiSearch().enabled,
    };

    // So we can abort requests later if necessary;
    // it's not a piece of state because it won't have
    // any effect on re-rendering the component.
    this.activeRequests = {};
    this.coarseGridPageLoadTracked = false;
    // Monotonically incremented on each fetchSections call so stale async
    // completions can detect they've been superseded and discard their results.
    this.fetchSectionsSequence = 0;
    // Identifies the search whose per-section asset lists are in state. A later
    // fetch that only reloads the selected section can keep the other sections'
    // matches while this key is unchanged (ESC-23162).
    this.sectionResultsCacheKeyValue = null;

    // Ref for the content area to manage focus for accessibility
    this.assetContentRef = React.createRef();
  }

  /** Taxonomy 2.0 coarse grid PageLoad (BRN-331); once per mount, surveys flag gated in amplitude service. */
  trackCoarseAssetGridPageLoadOnce = () => {
    if (this.coarseGridPageLoadTracked) return;

    const resourceType = BFG.resource?.type;
    if (resourceType !== 'brandfolder' && resourceType !== 'collection' && resourceType !== 'portal') {
      return;
    }

    let productArea;
    if (resourceType === 'portal') {
      productArea = 'PortalView';
    } else if (resourceType === 'collection') {
      productArea = 'CollectionView';
    } else {
      productArea = 'BrandfolderView';
    }

    amplitudeService.trackCoarsePageLoad({
      productArea,
      featureArea: 'AssetGrid',
      elementName: productArea,
    });
    this.coarseGridPageLoadTracked = true;
  };

  fetchInitialData() {
    this.fetchSections().then(() => {
      this.fetchLabels();
      this.fetchSearchableThings();
      this.updateEventListeners('add');
      this.fetchRequiredCustomFields();
      this.initializeCanva();
    });

    this.copyLabelsDrawerOpenSetting();
    this.addCustomFieldsSettingsListener();
  }

  handlePopState = () => {
    const params = this.urlParams();
    const q = params.q ? decodeURIComponent(params.q.replace(/[+]/g, ' ')) : '';

    if (!q && this.state.searchPageMode) {
      // back to main page from search
      this.setSearchPageBodyClass(false);
      this.setState((state) => ({
        searchPageMode: false,
        userViewOptions: { ...state.userViewOptions, showSections: true },
      }));
      this.submitSearchQuery(null, false, { skipHistoryPush: true });
      return;
    }

    if (this.state.searchPageMode) {
      this.submitSearchQuery(q || null, false, { skipHistoryPush: true, searchKey: q || `none-${Math.random()}` });
    }
  };

  setSearchPageBodyClass(active) {
    document.body.classList.toggle('search-page-mode', active);
  }

  componentDidMount() {
    this.setSearchPageBodyClass(this.state.searchPageMode);
    window.addEventListener('popstate', this.handlePopState);
    if (BFG.manifestDigest && BFG.manifest?.key) {
      fetchShareManifest(BFG.manifest.key).then((shareManifest) => {
        const expired = shareManifestExpired(shareManifest);
        this.setState({ internalShareLinkExpired: expired })
        if (!expired) {
          this.fetchInitialData();
        }
      });
    } else {
      this.fetchInitialData();
    }
  }

  componentWillUnmount() {
    this.updateEventListeners('remove');
    this.removeCustomFieldsSettingsListener();
    window.removeEventListener('popstate', this.handlePopState);
  }

  initializeCanva = async () => {
    const canvaEnabled = BFG.hasFeature('canva_button');
    if (!canvaEnabled) { return }

    const response = await fetchCanvaCredentials();
    const apiKey = response?.data?.key;
    if (apiKey) {
      this.showCanvaButton(apiKey);
    };
  };

  showCanvaButton = (apiKey) => {
    /**
    * https://docs.developer.canva.com/button/javascript/api-reference/initialize
    */
    const script = document.createElement('script');
    script.src = 'https://sdk.canva.com/designbutton/v2/api.js';
    script.onload = () => {
      (async () => {
        if (!window.Canva || !window.Canva.DesignButton) {
          return;
        }

        /**
         * IMPORTANT: api should only be initialized once, and must live here
         * at the top level containing component, otherwise multiple
         * Canva div's and iframe's load, making the integration unstable and unusable
         */
        const api = await window.Canva.DesignButton.initialize({
          apiKey: apiKey
        });

        this.setState({
          canvaApi: api
        });
      })();
    };
    document.body.appendChild(script);
  };

  getSection(sectionKey) {
    const { sections, assets } = this.state;
    const result = sections[sectionKey];
    if (result) {
      return result;
    }

    return {
      currentPage: 1,
      total: assets[sectionKey] ? assets[sectionKey].length : 0,
      totalPages: 1,
      loading: false,
    };
  }

  get resourceKey() {
    return Collection.key ? Collection.key : Brandfolder.key;
  }

  get resourceType() {
    return Collection.key ? "Collection" : "Brandfolder";
  }

  updateUserViewOptions = (option, updatedValue) => {
    let triggerFetchSections = false; // instantiate here so it's available in callback below

    this.setState((prevState) => {
      const { defaultSortOrder, paginatePer } = this.props;
      const { searchQuery, searchSubmitted } = prevState;
      const updatedState = { ...prevState.userViewOptions };
      const { assetsPerPage, searchOperator, selectedUGTLocale, sortOption } = updatedState;
      const previousValue = updatedState[option];
      const defaultValues = getDefaultViewOptions(defaultSortOrder, paginatePer);

      // reset to default values
      if (option === 'reset') {
        triggerFetchSections = sortOption !== defaultValues.sortOption
          || assetsPerPage !== defaultValues.assetsPerPage
          || selectedUGTLocale !== defaultValues.selectedUGTLocale
          || searchOperator !== defaultValues.searchOperator;
        return { userViewOptions: defaultValues };
      }

      // flip state for a toggle
      if (option === 'showCustomFields' || option === 'showEmptySections' || option === 'showSections' || option === 'showTaskDetails') {
        updatedState[option] = !previousValue;
        triggerFetchSections = option === 'showSections';

        return { userViewOptions: updatedState };
      }

      // if no change, avoid updating state and avoid rerender
      if (previousValue === updatedValue) {
        return null;
      }

      // update for a value that's a string
      if (option === 'selectedUGTLocale' || option === 'searchOperator') {
        triggerFetchSections = option === 'selectedUGTLocale' || (searchQuery && searchSubmitted);
        updatedState[option] = updatedValue;
        if (option === 'searchOperator') {
          this.clearSearchParams();
        }
        return { userViewOptions: updatedState };
      }

      if (option === 'sortOption' || option === 'assetsPerPage') {
        triggerFetchSections = (previousValue !== updatedValue);
      }

      updatedState[option] = updatedValue;
      return { userViewOptions: updatedState };
    }, () => {
      updateUserViewOptionsInLocalStorage(this.state.userViewOptions);
      if (triggerFetchSections) this.fetchSections();
    });
  }

  toggleFiltersDrawer = (source) => {
    this.setState((prevState) => {
      const { filtersDrawerOpen } = prevState;
      let updatedFiltersDrawerOpen = 'none';

      if (filtersDrawerOpen === 'none') { // was CLOSED
        if (source === 'labels-drawer') {
          updatedFiltersDrawerOpen = 'labels';
        } else {
          updatedFiltersDrawerOpen = 'advanced-filters';
        }
      }

      if (BFG.context.hasFeature("labels")) { // was OPEN
        if (source === 'labels-drawer' && filtersDrawerOpen === 'advanced-filters') {
          updatedFiltersDrawerOpen = 'labels';
        } else if (source === 'advanced-filters' && filtersDrawerOpen === 'labels') {
          updatedFiltersDrawerOpen = 'advanced-filters';
        } else if (source === 'section-search' && filtersDrawerOpen === 'labels') {
          updatedFiltersDrawerOpen = 'advanced-filters';
        }
      }

      setStorage(StorageTypes.Local, 'drawerOpen', updatedFiltersDrawerOpen);

      return { filtersDrawerOpen: updatedFiltersDrawerOpen };
    });
  }

  updateEventListeners = (action) => {
    const triggerActivateLabel = ({ detail }) => {
      const labelkey = detail;
      this.activateLabel(labelkey);
    };

    const toggleFilters = (e) => {
      if (e.key === 'b' && (e.getModifierState("Meta") || e.getModifierState("Control"))) {
        this.toggleFiltersDrawer('advanced-filters');
      }
    };

    const launchModal = (e, modalType, dismissDialog = true) => {
      const { assetKey, attachmentKey, downloadCallback } = e.detail;
      if (dismissDialog) {
        BF.dialog.dismiss();
      }
      this.setState({
        lastVisited: { assetKey, attachmentKey },
        [modalType]: true,
        downloadAlertCallback: downloadCallback,
      });
    };

    const updateResize = () => {
      this.setState({
        windowDimensions: {
          innerHeight: window.innerHeight,
          innerWidth: window.innerWidth,
        }
      });
    };

    // disable text highlighting when shift selecting assets
    // shoutout to https://stackoverflow.com/questions/1527751/disable-text-selection-while-pressing-shift
    const updateShift = (e) => {
      document.onselectstart = function () {
        return !(e.key == "Shift" && e.shiftKey);
      }
    }

    if (action === 'add') {
      if (!BFG.showPageLite) {
        window.addEventListener('activateLabel', triggerActivateLabel);
        window.addEventListener('keydown', toggleFilters);
      }
      window.addEventListener('resize', updateResize);
      window.addEventListener('launchAdvancedDownload', (e) => launchModal(e, 'showAdvancedDownloadModal'));
      window.addEventListener('launchWebToPrint', (e) => launchModal(e, 'showWebToPrintModal'));
      window.addEventListener('launchDownloadAlert', (e) => launchModal(e, 'showDownloadAlert', false));
      window.addEventListener('keyup', updateShift);
      window.addEventListener('keydown', updateShift);
    }

    if (action === 'remove') {
      if (!BFG.showPageLite) {
        window.removeEventListener('activateLabel', triggerActivateLabel);
        window.removeEventListener('keydown', toggleFilters);
      }
      window.removeEventListener('resize', updateResize);
      window.removeEventListener('launchAdvancedDownload', (e) => launchModal(e, 'showAdvancedDownloadModal'));
      window.removeEventListener('launchWebToPrint', (e) => launchModal(e, 'showWebToPrintModal'));
      window.removeEventListener('launchDownloadAlert', (e) => launchModal(e, 'showDownloadAlert', false));
      window.removeEventListener('keyup', updateShift);
      window.removeEventListener('keydown', updateShift);
    }

    return undefined;
  }

  clearSelected = () => {
    this.setState({
      entireSectionSelected: new Set(),
      lastSelected: null,
      selectedAssetKeys: new Set(),
      selectedViewOnlyAssetKeys: new Set()
    });
  }

  toggleSelected = (options = {}) => {
    const { assetKeys, sectionKey, selectAllPages, selectCurrentPage, viewOnlyAssetKeys } = options;
    const { downloadRequestId } = this.props;

    this.setState(({ entireSectionSelected, selectedAssetKeys, selectedViewOnlyAssetKeys }) => {
      /** new sets are required so we don't mutate state */
      const fullySelectedSections = new Set(entireSectionSelected);
      const selectedKeys = new Set(selectedAssetKeys);
      const selectedViewOnlyKeys = new Set(selectedViewOnlyAssetKeys);

      const keys = Array.isArray(assetKeys) ? assetKeys : [assetKeys];
      const viewOnlyKeys =
        !downloadRequestId && Array.isArray(viewOnlyAssetKeys)
          ? viewOnlyAssetKeys
          : !downloadRequestId && viewOnlyAssetKeys
            ? [viewOnlyAssetKeys]
            : [];

      let lastSelected;

      keys.forEach((assetKey) => {
        if (!selectCurrentPage && !selectAllPages && selectedKeys.has(assetKey)) {
          selectedKeys.delete(assetKey);
        } else {
          selectedKeys.add(assetKey);
        }

        lastSelected = assetKey;
      });

      viewOnlyKeys.forEach((viewOnlyAssetKey) => {
        if (!selectCurrentPage && !selectAllPages && selectedViewOnlyKeys.has(viewOnlyAssetKey)) {
          selectedViewOnlyKeys.delete(viewOnlyAssetKey);
        } else {
          selectedViewOnlyKeys.add(viewOnlyAssetKey);
        }
      });

      if (selectAllPages) {
        fullySelectedSections.add(sectionKey);
      } else if (sectionKey) {
        fullySelectedSections.delete(sectionKey);
      }

      return {
        entireSectionSelected: fullySelectedSections,
        lastSelected,
        selectedAssetKeys: selectedKeys,
        selectedViewOnlyAssetKeys: selectedViewOnlyKeys
      };
    });
  }

  toggleActiveSection = (sectionKey) => {
    const {
      activeSection,
      activeLabelKey,
      userViewOptions,
      searchQuery,
      filterStrings,
      sections: cachedSections,
    } = this.state;
    const { sections: allSections, sectionsToShow } = this.props;

    const updatedActiveSection = activeSection === sectionKey ? 'all' : sectionKey;

    // BRN-2330: When a filter/search is active, results are narrowed per-section,
    // so the cached "all sections" data is stale after switching sections. Force a
    // refetch in that case. (Routed through the lazy loader in fetchSections to
    // avoid firing N parallel section requests — see useLazyFetch below.)
    const hasActiveFilters = !!searchQuery || filterStrings.length > 0;

    // Search/filter fetches (and clearing a search) only populate the currently
    // selected section. Switching to another section — or back to All — would
    // otherwise render the empty cache fallback (0 assets).
    const sectionsExpectedInCache = sectionsToShow.length
      ? allSections.filter((section) => sectionsToShow.includes(section.id))
      : allSections;
    const targetMissingFromCache = updatedActiveSection === 'all'
      ? sectionsExpectedInCache.some((section) => !cachedSections[section.section_key])
      : !cachedSections[updatedActiveSection];

    this.setState({ activeSection: updatedActiveSection }, () => {
      if (!userViewOptions?.showSections || activeLabelKey || hasActiveFilters || targetMissingFromCache) {
        this.fetchSections();
      }
    });
  }

  addRemovePinnedSearch = async (query) => {
    const { searchFilters, searchQuery: stateQuery } = this.state;
    const searchQuery = query || stateQuery;
    if (searchFilters.map((filter) => filter.query).includes(searchQuery)) {
      const pinnedSearch = [...searchFilters].filter((item) => item.query === searchQuery);
      const updatedFilters = [...searchFilters].filter((filter) => (filter.key !== pinnedSearch[0].key));

      removeFilter(pinnedSearch[0].key);
      this.setState({ searchFilters: updatedFilters });
    } else {
      this.showFilterModal(searchQuery);
    }
  }

  /**
   * SMHBR-AMP186: After Search is activated, move focus to the results heading
   * (or the results region / no-results message) so keyboard and screen reader
   * users land on the updated content instead of the previous control (Filters).
   */
  focusSearchResults = () => {
    const container = this.assetContentRef.current;
    if (!container) {
      return;
    }

    const heading = container.querySelector('#section-title-search-results-assets')
      || container.querySelector('.section-title')
      || container.querySelector('.no-results-copy');
    const target = heading || container;

    if (typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }
  }

  announceSearchOutcome = () => {
    const { activeSection, sections, searchQuery, userViewOptions } = this.state;
    if (!searchQuery) {
      return;
    }

    let count = 0;
    if (!userViewOptions?.showSections && activeSection === 'all') {
      count = sections['search-results-assets']?.total || 0;
    } else {
      count = Object.values(sections).reduce((sum, section) => (
        sum + (section?.total || 0)
      ), 0);
    }

    const searchResultsAnnouncement = count === 0
      ? t`Search complete. No results found.`
      : t`Search complete. ${count} results.`;

    this.setState({ searchResultsAnnouncement });
  }

  submitSearchQuery = (filter, keypress, options = {}) => {
    let searchKey = options.searchKey || 'none';
    const { searchFilters } = this.props;
    const { searchPageMode } = this.state;
    const shouldFocusResults = Boolean(filter);

    if (semanticSearchFeature() && this.state.aiSearchEnabled && !options.skipHistoryPush) {
      if (filter) {
        const newUrl = `${window.location.pathname}?q=${encodeURIComponent(filter)}`;
        window.history.pushState({}, document.title, newUrl);
        if (!searchPageMode) {
          this.setSearchPageBodyClass(true);
          this.setState((state) => ({
            activeSection: 'all',
            activeLabelKey: null,
            filters: { ...defaultFilters() },
            filterStrings: [],
            filtersDrawerOpen: 'none',
            peopleSearchIdentityId: null,
            searchFilterOperators: defaultSearchFilterOperators,
            searchPageMode: true,
            userViewOptions: {
              ...state.userViewOptions,
              showSections: false,
              ...(getAiSearch().enabled ? { sortOption: SortOptions.Relevancy } : {}),
            },
          }));
        }
      } else if (searchPageMode && window.location.search) {
        window.history.pushState({}, document.title, window.location.pathname);
      }
    }


    this.setState((state) => {
      const isPinnedSearch = searchFilters.filter((item) => item.query === filter);
      const searchQuery = filter === state.searchQuery && isPinnedSearch.length > 0 && !keypress ? null : filter;

      if (searchQuery) {
        Insight.trackSearchEvent(searchQuery);
      }

      if (searchQuery && window.amplitudeService) {
        const now = Date.now();
        window.amplitudeService.trackEvent('Search Initiation', {
          'event group': 'Search',
          'key properties': {
            userId: BFG.currentUser?.user_id,
            userRole: BFG.currentUser?.role,
            orgSlugId: BFG.org_slug,
            orgName: BFG.org_name,
            nlsEnabled: isAiSearchOn(),
            eapEnabled: semanticSearchFeature(),
            brandfolderName: BFG.resource?.name,
            pageUrl: window.location.href,
            timestamp: now,
            dateTime: new Date(now).toLocaleString('en-GB').replace(',', ''),
            searchTerm: searchQuery,
          },
        });
      }

      if (state.searchQuery && searchQuery && window.BFG?.amplitude?.enabled && window.amplitudeService) {
        const now = Date.now();
        const withinWindow = state.lastSearchTimestamp &&
          (now - state.lastSearchTimestamp) / 1000 <= QUERY_REFINEMENT_WINDOW_SECONDS;

        if (withinWindow) {
          const previousWords = state.searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
          const newWords = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
          const isRefinement = newWords.some(word => previousWords.includes(word));

          if (isRefinement) {
            window.amplitudeService.trackEvent('SearchRefined', {
              elementName: 'Search',
              elementLocation: 'PrimarySearch',
              productArea: 'BrandfolderView',
              featureArea: 'PrimarySearch',
              eventName: 'SearchRefined',
              'key properties': {
                previousQuery: state.searchQuery,
                newQuery: searchQuery,
                searchType: semanticSearchFeature() && this.state.aiSearchEnabled && searchQuery ? 'nls' : 'keyword'
              }
            });
          }
        }
      }

      if (!searchQuery) {
        // This may seem weird, but it's actually pretty great;
        // We use searchKey as the input to the `key` prop on the
        // section search. When it changes, the section search re-mounts
        // and updates its own internal state based on its props.
        // In cases where we clear the search, it's best to set it to
        // something unique to force this refresh. Random works pretty ok.
        searchKey = `none-${Math.random()}`;
      }

      if (filter === null || !searchQuery) {
        this.clearSearchParams();
      }

      return {
        loading: true,
        ...(filter === null || !searchQuery ? { peopleSearchIdentityId: null } : {}),
        ...options?.resetSearchOperator && {
          userViewOptions: {
            ...state.userViewOptions,
            searchOperator: isAiSearchOn()
              ? (SearchOperatorOptions[BFG?.semanticSearchSettings?.default_semantic_search_operator]
                || SearchOperatorOptions.Strict)
              : (BFG?.brandfolderSettings?.default_search_operator || SearchOperatorOptions.OR),
          }
        },
        lastSearchTimestamp: searchQuery ? Date.now() : null,
        searchQuery,
        searchResultsAnnouncement: '',
        searchSubmitted: !!filter,
        recievedSections: 0,
        searchKey,
        sections: {},
      };
    }, () => {
      // Move focus immediately on submit so it cannot fall back to Filters.
      if (shouldFocusResults) {
        this.focusSearchResults();
      }
      this.fetchSections().then(() => {
        if (shouldFocusResults) {
          this.focusSearchResults();
          this.announceSearchOutcome();
        }
      });
    });
  }

  clearSearchParams = () => {
    // when clicking on clear search, changing the search operator, or deleting the search input
    // (only after coming to the page from a search share link)
    // we want to remove any query strings from our url
    // because otherwise refreshing brings back the search
    // which may be unexpected for the user
    if (window.location.search && !this.state.searchPageMode) {
      window.history.replaceState({}, document.title, `${window.location.protocol}//${window.location.host}${window.location.pathname}`);
    }
  }

  resetFilters = () => {
    this.setState({
      activeSection: 'all',
      filters: { ...defaultFilters() },
      filterStrings: [],
      peopleSearchIdentityId: null,
      searchFilterOperators: defaultSearchFilterOperators,
    }, this.fetchSections);
  }

  updateSearchFilterOperator = (filterType, operatorUpdate) => {
    // affects tags and custom fields as of 3/2021
    this.setState((prevState) => ({
      searchFilterOperators: {
        ...prevState.searchFilterOperators,
        ...filterType === FilterType.Tags && { tags: operatorUpdate },
        ...filterType === FilterType.CustomFields && {
          custom_fields: {
            ...prevState.searchFilterOperators.custom_fields,
            ...operatorUpdate,
          }
        },
      }
    }), () => {
      const { filters } = this.state;
      if (filters.tags?.length || Object.keys(filters?.custom_fields).length) {
        this.fetchSections();
      }
    });
  }

  updateFilters = (filterType, keyValue, subValue) => {
    const { filters, filterStrings } = this.state;
    if (filterType === 'reset') {
      this.resetFilters();
      return;
    }

    const results = manageFilters({
      filters,
      filterStrings,
      filterType,
      keyValue,
      subValue,
    });

    this.setState({
      ...results,
      loading: true,
    }, this.fetchSections);
  }

  activateLabel = (labelId) => {
    this.setState({ activeLabelKey: labelId }, () => {
      this.fetchSections();
    });
  }

  addRemoveSelected = (assetKeys, viewOnlyAssetKeys) => {
    this.setState(({ entireSectionSelected }) => {
      const update = {
        selectedAssetKeys: new Set(assetKeys),
        entireSectionSelected: assetKeys.length ? entireSectionSelected : new Set(),
        selectedViewOnlyAssetKeys: new Set(viewOnlyAssetKeys || [])
      };
      if (!assetKeys.length) update.lastSelected = null;
      return update;
    });
  }

  selectAll = () => {
    this.setState({
      selectedAssetKeys: new Set(this.allAssetKeys()),
      selectedViewOnlyAssetKeys: new Set(this.allViewOnlyAssetKeys())
    });
  }

  /** Clicking "Select: All Visible" from the BulkSelectBar.jsx */
  selectAllVisible = () => {
    this.setState((prevState) => ({
      selectedAssetKeys: new Set([...prevState.selectedAssetKeys, ...this.allVisibleAssetKeys()]),
      selectedViewOnlyAssetKeys: new Set([...prevState.selectedViewOnlyAssetKeys, ...this.allVisibleViewOnlyAssetKeys()])
    }));
  }

  shiftSelect = (assetKey, _, viewOnlyAssetKeys) => {
    // TODO: just redo all of this :(
    const { lastSelected, selectedAssetKeys } = this.state;
    if (!lastSelected) {
      this.toggleSelected({
        assetKeys: assetKey,
        viewOnlyAssetKeys
      });
      return undefined;
    }

    const allAssetKeys = this.allAssetKeys();
    const lastSelectedPosition = allAssetKeys.indexOf(lastSelected);
    const position = allAssetKeys.indexOf(assetKey);
    const selectedSet = new Set(selectedAssetKeys); // TODO: how do I get this, but ORDERED?

    if (position !== lastSelectedPosition) {
      allAssetKeys.forEach((asset, i) => {
        const assetPosition = i;

        if ((assetPosition > lastSelectedPosition && assetPosition <= position)
          || (assetPosition < lastSelectedPosition && assetPosition >= position)
        ) {
          if (selectedSet.has(asset)) {
            selectedSet.delete(asset);
          } else {
            selectedSet.add(asset);
          }
        }
      });
      this.addRemoveSelected(Array.from(selectedSet));
    } else {
      this.toggleSelected({
        assetKeys: assetKey,
        viewOnlyAssetKeys
      });
    }
    this.setState({ lastSelected: assetKey });
    return undefined;
  }

  onNewAssets = (sectionKey, { data: newAssets }) => {
    this.setState((state) => {
      const { sections, assets, userViewOptions } = state;
      const { paginatePer } = this.props;
      const { sortOption } = userViewOptions;
      let { assetsPerPage } = userViewOptions;
      const assetsPerPageParam = this.urlParams().per && parseInt(this.urlParams().per, 10);

      // priority is url param, then user defined option, then plan admin defined value
      assetsPerPage = assetsPerPageParam || assetsPerPage || paginatePer;

      const sectionData = this.getSection(sectionKey);
      let { totalPages } = sectionData;
      const { total } = sectionData;

      // Possible race condition where the section is being read before its initialized, return [] in this case
      let updatedAssets = assets[sectionKey] ? [...assets[sectionKey]] : [];

      const { selectedAssetKeys } = state;
      const newAssetsWithNewFlag = newAssets.map((newAsset) => {
        selectedAssetKeys.add(newAsset.id);
        return { ...newAsset, isNewAsset: true };
      });

      BF.fx.updateAssetPageCount(newAssetsWithNewFlag.length, true);

      const newTotal = total + newAssetsWithNewFlag.length;
      if (newTotal > assetsPerPage) {
        totalPages = Math.trunc(newTotal / assetsPerPage) + 1;
      }

      // user selected sort option determines how new assets are added to section
      // if needsSorting === false, new assets are simply added to the end of existing assets
      const newAssetsNeedSorting = 'created_at DESC,updated_at DESC,name ASC,name DESC'.includes(sortOption);
      const spaceOnPageForNewAssets = totalPages <= 1 && total < assetsPerPage;
      let updatedAssetsState;

      // update assets displayed in section on show page
      if (newAssetsNeedSorting || spaceOnPageForNewAssets) {
        if (newAssetsNeedSorting) {
          // add all new assets to beginning of section
          // for 'created_at DESC' and 'updated_at DESC', assets are correctly sorted with this addition
          // for 'name ASC' and 'name DESC', assets still need sorting
          updatedAssets = [...newAssetsWithNewFlag, ...updatedAssets];
        }

        if (sortOption === 'name ASC' || sortOption === 'name DESC') {
          const sortFactor = sortOption === 'name DESC' ? -1 : 1;
          const sortingFunction = (a, b) => {
            const nameA = a?.attributes?.name?.toUpperCase();
            const nameB = b?.attributes?.name?.toUpperCase();
            let comparison = 0;
            if (nameA > nameB) comparison = 1;
            if (nameB > nameA) comparison = -1;
            return comparison * sortFactor;
          };
          updatedAssets.sort(sortingFunction);
        }

        if (!newAssetsNeedSorting) {
          updatedAssets = [...updatedAssets, ...newAssetsWithNewFlag];
        }

        // remove extra assets if asset quantity exceeds the allowed assetsPerPage
        if (updatedAssets.length > assetsPerPage) {
          updatedAssets = updatedAssets.filter((asset, i) => (i < assetsPerPage));
        }

        updatedAssetsState = {
          ...assets,
          [sectionKey]: updatedAssets,
        };
      }

      return {
        ...updatedAssetsState && { assets: updatedAssetsState },
        sections: {
          ...sections,
          [sectionKey]: {
            ...sectionData,
            total: newTotal,
            totalPages,
          }
        },
        selectedAssetKeys,
      };
    });
  }

  copyLabelsDrawerOpenSetting = () => {
    /* copy old labels drawer setting into new drawerOpen setting */
    const getConvertedLabelsDrawerOpen = () => {
      const oldStoredLabelsDrawerOpen = getStorage(StorageTypes.Local, 'labelsDrawerOpen'); // old labels-specific value
      switch (oldStoredLabelsDrawerOpen) {
        case true:
          return 'labels';
        case false:
          return 'none';
        default:
          return null;
      }
    };

    if (!getStorage(StorageTypes.Local, 'drawerOpen')) {
      const oldLabelsDrawerOpen = getConvertedLabelsDrawerOpen();
      if (oldLabelsDrawerOpen !== null) {
        setStorage(StorageTypes.Local, 'drawerOpen', oldLabelsDrawerOpen);
      }
    }
  }

  fetchRequiredCustomFields = async () => {
    if (this.props.controlledCustomFieldsEnabled) {
      // impossible to have required custom fields if controlled custom fields not enabled
      const response = await getCustomFieldKeys({
        params: {
          include: 'dependent_custom_fields',
          order: 'asc',
          per: 3000,
          ugt_locale: determineUGTLocale(),
          fields: 'multi_value_enabled',
          sort_by: 'name',
        },
        resourceKey: BFG.resource.key,
        resourceType: BFG.resource.type
      });

      const customFieldKeys = flattenCustomFieldKeysList(response);
      const dependentCustomFields = response.included?.map((dependentCustomField) => dependentCustomField.attributes) || [];
      this.setState({ customFieldKeys, dependentCustomFields });
    }
  };

  handleCustomFieldsSettingsUpdated = (e) => {
    if (e?.detail?.refresh) {
      this.fetchRequiredCustomFields();
    }
  };

  addCustomFieldsSettingsListener = () => {
    window.addEventListener(CustomFieldsEvents.SettingsUpdated, this.handleCustomFieldsSettingsUpdated);
  }

  removeCustomFieldsSettingsListener = () => {
    window.removeEventListener(CustomFieldsEvents.SettingsUpdated, this.handleCustomFieldsSettingsUpdated);
  }

  fetchLabels = () => {
    if (!BFG.context.hasFeature("labels") || BFG.showPageLite) { return undefined; }
    let labelsTree; // initialize here to make available in callback
    getLabels({ fetchAll: true }).then((response) => {
      labelsTree = formatLabelsTree(response);
      const labelsFlat = formatLabelsFlat(response);
      this.setState({
        labels: {
          labelsFlat,
          labelsResponse: response,
          labelsTree,
        }
      }, () => {
        const hasLabels = labelsTree.children && labelsTree.children.length > 0;
        const drawerOpenLocal = getStorage(StorageTypes.Local, 'drawerOpen');

        // if user has not toggled the 'labels' or 'advanced-filters' drawer, start with the labels drawer open
        if (hasLabels && drawerOpenLocal === null) {
          this.setState({ filtersDrawerOpen: 'labels' });
        }
      });
    });

    return undefined;
  }

  fetchSearchableThings = () => {
    if (BFG.context.hasFeature("disable_advanced_filters") || BFG.showPageLite) { return undefined; }

    getSearchableThings({
      resourceType: window.SOURCE.resource_type,
      resourceKey: window.SOURCE.resource_key,
      ugtLocale: this.state.userViewOptions.selectedUGTLocale
    }).then((searchableThings) => { this.setState({ searchableThings }); });
    return undefined;
  }

  // Fingerprint of everything that changes which sections contain matches.
  // Section selection is intentionally excluded: switching sections refetches
  // one section, and the other sections' matches are still valid.
  sectionResultsCacheKey({
    activeLabelKey,
    aiSearchEnabled,
    filters,
    peopleSearchIdentityId,
    searchFilterOperators,
    searchQuery,
    searchSubmitted,
    userViewOptions,
  }) {
    const searchOperator = userViewOptions?.searchOperator;
    const useSemantic = semanticSearchFeature() && aiSearchEnabled && searchSubmitted;
    const searchQueryForLogic = useSemantic
      ? searchQuery
      : this.escapedSearchQuery(searchQuery, searchOperator);
    const search = filterLogic(filters, searchQueryForLogic || '', searchFilterOperators);

    return JSON.stringify({
      activeLabelKey: activeLabelKey || null,
      aiSearchEnabled: Boolean(aiSearchEnabled),
      peopleIds: (filters?.people || []).map((person) => person.id).sort(),
      peopleSearchIdentityId: peopleSearchIdentityId || null,
      search,
      selectedUGTLocale: userViewOptions?.selectedUGTLocale || null,
      showSections: Boolean(userViewOptions?.showSections),
    });
  }

  // Copy asset lists for sections this fetch did not reload, when the search
  // is the same one that produced them. Otherwise a selected-section refetch
  // replaces the map and every other matching section looks empty.
  retainUnfetchedSectionResults(resultsCacheKey, prior, next) {
    if (resultsCacheKey !== this.sectionResultsCacheKeyValue) {
      return next;
    }

    const assets = { ...prior.assets };
    const sections = { ...prior.sections };
    const tasks = { ...prior.tasks };
    const vsAttachmentIds = { ...prior.vsAttachmentIds };

    Object.keys(next.assets).forEach((sectionKey) => {
      assets[sectionKey] = next.assets[sectionKey];
      if (next.sections[sectionKey]) {
        sections[sectionKey] = next.sections[sectionKey];
      }
      if (next.tasks[sectionKey]) {
        tasks[sectionKey] = next.tasks[sectionKey];
      } else {
        delete tasks[sectionKey];
      }
      if (next.vsAttachmentIds[sectionKey]) {
        vsAttachmentIds[sectionKey] = next.vsAttachmentIds[sectionKey];
      } else {
        delete vsAttachmentIds[sectionKey];
      }
    });

    return {
      assets,
      sections,
      tasks,
      vsAttachmentIds,
    };
  }

  fetchSections = async () => {
    const sequenceId = ++this.fetchSectionsSequence;
    this.setState({ loading: true });
    let { sections, sectionsToShow } = this.props;
    const {
      activeLabelKey,
      activeSection,
      aiSearchEnabled,
      filters,
      filterStrings,
      peopleSearchIdentityId,
      searchFilterOperators,
      searchQuery,
      searchSubmitted,
      userViewOptions,
    } = this.state;
    const priorSectionResults = {
      assets: this.state.assets,
      sections: this.state.sections,
      tasks: this.state.tasks,
      vsAttachmentIds: this.state.vsAttachmentIds,
    };
    const resultsCacheKey = this.sectionResultsCacheKey({
      activeLabelKey,
      aiSearchEnabled,
      filters,
      peopleSearchIdentityId,
      searchFilterOperators,
      searchQuery,
      searchSubmitted,
      userViewOptions,
    });
    const newAssets = {};
    const newSections = {};
    const newTasks = {};
    const newVsAttachmentIds = {};
    let newNlsScores = {};
    let newNlsEsQuery = null;

    const storedPersonId = sessionStorage.getItem('people_search_person_id');
    if (storedPersonId) {
      sessionStorage.removeItem('people_search_person_id');
      this.setState({ peopleSearchIdentityId: storedPersonId });
    }

    if (!userViewOptions?.showSections) {
      if (activeSection !== 'all') {
        sections = sections.filter((section) => section.section_key === activeSection);
      } else {
        // for sectionless, create 1 section with the minimum attributes needed to render it
        sections = [{
          id: searchQuery ? 'search-results-assets-id' : 'show-page-assets-id',
          section_key: searchQuery ? 'search-results-assets' : 'show-page-assets',
        }];
        sectionsToShow = [sections[0].id];
      }
    } else if (activeSection !== 'all') {
      sections = sections.filter((section) => section.section_key === activeSection);
    }

    // When a label/filter/search is active and sections are visible, lazy-load
    // sections to avoid firing N parallel API calls that trigger 429 rate-limit
    // errors. Only the first few sections are fetched eagerly; the rest are
    // deferred until the Section component scrolls into the viewport and triggers
    // onUpdate (via SectionPlaceholder's IntersectionObserver).
    // BRN-2330: extended beyond labels to also cover active filters/search, so
    // switching sections under "Organize by section" refreshes results without
    // a parallel-request storm.
    const useLazyFetch = BFG.resource.type === 'brandfolder'
      && !BFG.resource.is_workspace
      && userViewOptions?.showSections
      && (activeLabelKey || searchQuery || filterStrings.length > 0)
      && sections.length > 1;

    const EAGER_SECTION_COUNT = 3;

    let visibleSections = sections.filter((s) => sectionsToShow.includes(s.id));

    // Prefetch per-section asset counts to determine which sections to load:
    // - When "show empty sections" is OFF: filter out empty sections before lazy loading
    // - When a search query is active: identify exactly which sections have matching
    //   results so we can fetch them all eagerly (avoids premature "No results found")
    // TODO (BRN-2330): getSectionAssetCounts is label-aware only; for the
    // filter-only path it does not yet narrow by the active custom-field/tag
    // filter, so empty-under-filter sections may still be fetched eagerly.
    // Results still load correctly on scroll; revisit to pass the filter query.
    const needsCounts = useLazyFetch
      && (userViewOptions?.showEmptySections === false || searchQuery);

    if (needsCounts) {
      try {
        const counts = await getSectionAssetCounts(
          { ...activeLabelKey && { labelKeys: [activeLabelKey] } },
          { resourceKey: BFG.resource.key }
        );
        visibleSections = visibleSections.filter(
          (s) => (counts[s.section_key] || 0) > 0
        );
      } catch (e) {
        // On failure, proceed with all sections (graceful degradation)
      }
    }

    // When search is active, the counts call already narrowed visibleSections to
    // only those with results — fetch them all eagerly (no deferral needed).
    const shouldDeferSections = useLazyFetch && !searchQuery;

    const deferredSectionKeys = shouldDeferSections
      ? new Set(visibleSections.slice(EAGER_SECTION_COUNT).map((s) => s.section_key))
      : new Set();

    // Track deferred sections so fetchSection can pick them up on viewport entry
    this.deferredLabelSections = deferredSectionKeys;

    const promises = visibleSections.map((section) => {
      const { section_key: sectionKey } = section;

      if (deferredSectionKeys.has(sectionKey)) {
        newSections[sectionKey] = { total: 0, totalPages: 0, currentPage: 1, deferred: true };
        newAssets[sectionKey] = [];
        return undefined;
      }

      return this.updateSection(sectionKey, {
        identityId: storedPersonId,
      }).then(({ assets, included, vsAttachmentIds, nlsScores, nlsEsQuery, ...sectionData }) => {
        newAssets[sectionKey] = assets;
        newSections[sectionKey] = sectionData;
        if (vsAttachmentIds?.length) newVsAttachmentIds[sectionKey] = vsAttachmentIds;
        if (nlsScores && Object.keys(nlsScores).length) newNlsScores = { ...newNlsScores, ...nlsScores };
        if (nlsEsQuery) newNlsEsQuery = nlsEsQuery;
        const tasks = included ? included.filter(({ type }) => type === "tasks") : [];
        if (tasks.length > 0) {
          newTasks[sectionKey] = tasks;
        }
      });
    });

    try {
      await Promise.all(promises);
    } catch (e) {
      if (e.name === 'AbortError') {
        // this is fine, just means the user navigated away from the page before the request finished.
        // Only reset loading when this is still the active fetch — a stale fetch was aborted by a
        // newer one (triple-click race), so the newer fetch is still loading and must not be interrupted.
        if (sequenceId === this.fetchSectionsSequence) {
          this.setState({ loading: false });
        }
        return;
      } else if (e.status === 400 && searchQuery) {
        // A 400 status is returned from elastic search when the query is invalid
        Notify.create({
          title: t`Your search query is invalid.`,
          type: "error"
        });
      } else {
        Notify.create({
          title: t`Something went wrong. Please try again.`,
          type: "error"
        });
      }
    }

    if (sequenceId !== this.fetchSectionsSequence) return;

    // ESC-23162: a section click refetches only that section. Keep the other
    // sections' matches so their pills stay dark for this same search.
    const retained = this.retainUnfetchedSectionResults(resultsCacheKey, priorSectionResults, {
      assets: newAssets,
      sections: newSections,
      tasks: newTasks,
      vsAttachmentIds: newVsAttachmentIds,
    });
    this.sectionResultsCacheKeyValue = resultsCacheKey;

    return await new Promise((resolve) => {
      this.setState(
        {
          assets: retained.assets,
          sections: retained.sections,
          loading: false,
          tasks: retained.tasks,
          vsAttachmentIds: retained.vsAttachmentIds,
          nlsScores: newNlsScores,
          nlsEsQuery: newNlsEsQuery,
        },
        () => {
          this.trackCoarseAssetGridPageLoadOnce();

          // Track search result count with Amplitude after results are loaded
          if (searchQuery && window.BFG?.amplitude?.enabled && window.amplitudeService) {
            const searchSectionKey = 'search-results-assets';
            const searchSection = newSections[searchSectionKey];
            const resultCount = searchSection?.total || 0;

            // Only track if we have search results data
            if (searchSection) {
              window.amplitudeService.trackEvent('SearchExecute', {
                elementName: 'Search',
                elementLocation: 'PrimarySearch',
                productArea: 'BrandfolderView',
                featureArea: 'PrimarySearch',
                eventName: 'SearchExecute',
                'key properties': {
                  searchTerm: searchQuery,
                  resultCount: resultCount,
                  searchType: semanticSearchFeature() && this.state.aiSearchEnabled && searchQuery ? 'nls' : 'keyword',
                  resultConsistency: searchSection?.resultConsistency || null,
                  resultHash: searchSection?.resultHash || null,
                }
              });
            }
          }

          if (searchQuery && window.amplitudeService) {
            const searchSectionKey = 'search-results-assets';
            const searchSection = newSections[searchSectionKey];
            if (searchSection) {
              const now = Date.now();
              const resultsCount = searchSection?.total || 0;

              window.amplitudeService.trackEvent('Search Results Page View', {
                'event group': 'Search',
                'key properties': {
                  userId: BFG.currentUser?.user_id,
                  userRole: BFG.currentUser?.role,
                  orgSlugId: BFG.org_slug,
                  orgName: BFG.org_name,
                  nlsEnabled: isAiSearchOn(),
                  eapEnabled: semanticSearchFeature(),
                  brandfolderName: BFG.resource?.name,
                  pageUrl: window.location.href,
                  timestamp: now,
                  dateTime: new Date(now).toLocaleString('en-GB').replace(',', ''),
                  searchTerm: searchQuery,
                  wordsInTerm: searchQuery.trim().split(/\s+/).length,
                  charactersInTerm: searchQuery.length,
                  resultsCount,
                },
              });

              if (resultsCount === 0) {
                window.amplitudeService.trackEvent('Zero Results', {
                  'event group': 'Search',
                  'key properties': {
                    userId: BFG.currentUser?.user_id,
                    userRole: BFG.currentUser?.role,
                    orgSlugId: BFG.org_slug,
                    orgName: BFG.org_name,
                    nlsEnabled: isAiSearchOn(),
                    eapEnabled: semanticSearchFeature(),
                    brandfolderName: BFG.resource?.name,
                    pageUrl: window.location.href,
                    timestamp: now,
                    dateTime: new Date(now).toLocaleString('en-GB').replace(',', ''),
                    searchTerm: searchQuery,
                    wordsInTerm: searchQuery.trim().split(/\s+/).length,
                    charactersInTerm: searchQuery.length,
                    errorMessage: isAiSearchOn() ? 'No assets found' : 'No results found',
                  },
                });
              }
            }
          }

          resolve();
        }
      );
    });
  }

  fetchSection = async (sectionKey, options = {}) => {
    const { currentPage } = this.state.sections[sectionKey] || {};

    const { page: page_ } = options;
    const resolvedPage = page_ ?? currentPage ?? 1;

    this.setState((state) => ({
      sections: {
        ...state.sections,
        [sectionKey]: {
          ...state.sections[sectionKey],
          loading: true,
        },
      },
    }));

    let result;
    try {
      result = await this.updateSection(sectionKey, {
        page: resolvedPage,
      });
    } catch (e) {
      if (e.name === 'AbortError') {
        return;
      }
      throw e;
    }

    const { assets, included, vsAttachmentIds, ...sectionData } = result;

    // if you update tasks here, you'll also want to update tasks inside fetchSections above
    const tasks = included ? included.filter(({ type }) => type === "tasks") : [];

    this.setState((state) => ({
      assets: {
        ...state.assets,
        [sectionKey]: assets,
      },
      vsAttachmentIds: {
        ...state.vsAttachmentIds,
        [sectionKey]: vsAttachmentIds || [],
      },
      sections: {
        ...state.sections,
        [sectionKey]: {
          ...sectionData,
          loading: false,
        },
      },
      tasks: {
        ...state.tasks,
        [sectionKey]: tasks
      }
    }));
  }

  buildSectionAssetParams = (sectionKey, options = {}) => {
    const { defaultSortOrder } = this.props;

    const {
      activeLabelKey,
      aiSearchEnabled,
      filters,
      peopleSearchIdentityId,
      searchSubmitted,
      searchFilterOperators,
      searchQuery,
      userViewOptions,
      vsAttachmentIds,
      nlsEsQuery,
    } = this.state;

    const {
      assetsPerPage,
      searchOperator,
      selectedUGTLocale,
      sortOption
    } = userViewOptions;

    const {
      page: page_,
      per: perOverride,
      identityId: identityIdOption,
      ...restOptions
    } = options;
    const identityId = identityIdOption || peopleSearchIdentityId;
    const per = perOverride ?? (this.urlParams().per || assetsPerPage || this.props.paginatePer);
    const { currentPage } = this.state.sections[sectionKey] || {};
    const page = page_ ?? currentPage ?? 1;

    const option = getSortOption(searchSubmitted, sortOption, defaultSortOrder);
    const [sort_by, order] = option.split(' ');

    const useSemantic = semanticSearchFeature() && aiSearchEnabled && searchSubmitted;
    // In semantic mode pass raw searchQuery to filterLogic — Strict/Quoted escaping wraps
    // the whole query in double quotes which breaks field syntax like tags.strict:"x".
    const searchQueryForLogic = useSemantic ? searchQuery : this.escapedSearchQuery(searchQuery, searchOperator);
    const search = filterLogic(filters, searchQueryForLogic, searchFilterOperators);
    // In semantic mode, `semantic_query` carries the raw search bar text (no filter syntax)
    // and `active_filters` carries filter panel state structured. Backend semantic path uses
    // those; regular ES path continues to use `search` as-is.
    const semanticQuery = useSemantic ? searchQuery : null;

    return {
      ...restOptions,
      ...BFG.manifestDigest && { digest: BFG.manifestDigest },
      ...BFG.downloadRequestKey && { download_request_key: BFG.downloadRequestKey },
      ...!BFG.resource.is_workspace && { fast_jsonapi: true },
      fields: 'active_cdn_tracers,asset_data,assigned_users,attachment_count,availability,background_color,extension,html,position,printui_editor_link,prioritized_custom_fields,storyteq_editor_link,design_huddle_editor_link,tag_names,task_status,type,view_only,content_automation_editor_link,people_name,section_key',
      include: 'task',
      localUTC: Date.now(),
      ...activeLabelKey && { labelKeys: [activeLabelKey] },
      order,
      page,
      per,
      ...search && { search },
      ...search && {
        search_operator: (searchOperator === SearchOperatorOptions.Strict || searchOperator === SearchOperatorOptions.Quoted)
          ? SearchOperatorOptions.OR
          : searchOperator
      },
      sort_by,
      strict_search: searchOperator === SearchOperatorOptions.Strict,
      ...selectedUGTLocale && { ugt_locale: selectedUGTLocale },
      ...(filters.people && filters.people.length > 0
        ? { identity_ids: filters.people.map((p) => p.id) }
        : (identityId && { identity_ids: [identityId] })),
      ...(page > 1 && vsAttachmentIds[sectionKey]?.length && {
        vs_attachment_ids: vsAttachmentIds[sectionKey],
      }),
      ...(useSemantic && page > 1 && nlsEsQuery && { nls_es_query: nlsEsQuery }),
      ...(useSemantic && { semantic_query: semanticQuery }),
      ...(semanticSearchFeature() && aiSearchEnabled && { active_filters: filters }),
      ...(semanticSearchFeature() && { ai_search: aiSearchEnabled }),
    };
  };

  updateSection = (sectionKey, options = {}) => {
    const { activeSection, userViewOptions, filters, searchQuery, peopleSearchIdentityId } = this.state;
    const { showSections } = userViewOptions;
    const { identityId: identityIdOption } = options;
    const identityId = identityIdOption || peopleSearchIdentityId;

    const params = this.buildSectionAssetParams(sectionKey, options);

    const effectivePage = params.page ?? 1;
    const peopleSearchType = resolvePeopleSearchExecuteSearchType({
      filters,
      searchQuery: searchQuery || '',
      identityIdFromFace: identityId || null,
    });
    const usesPeopleSearch =
      BFG.context.hasFeature('people_tagging') &&
      BFG.brandfolderSettings?.people_tagging &&
      (filters.people?.length > 0 ||
        identityId ||
        (searchQuery && searchQuery.includes('people.strict:')));
    if (
      usesPeopleSearch &&
      peopleSearchType &&
      effectivePage === 1 &&
      amplitudeService.isReady()
    ) {
      const eventArea = 'AppFrontend';
      const eventName = 'SearchExecute';
      const productArea = resolvePeopleTaggingHostProductArea();
      const featureArea = 'BrandfolderView';
      const elementName = 'PeopleSearchFilter';
      const elementLocation = 'PeopleFilterPanel';
      const eventFull = `${eventArea}.${productArea}.${featureArea}.${elementName}.${eventName}`;
      amplitudeService.trackEvent(eventName, {
        eventArea,
        productArea,
        featureArea,
        elementLocation,
        elementName,
        eventName,
        eventFull,
        brandfolderId: BFG.brandfolder_key,
        organizationId: BFG.organization_key,
        userId: BFG.currentUser?.user_id,
        userRole: BFG.currentUser?.role,
        searchType: peopleSearchType,
      });
    }

    const activeRequest = this.activeRequests[sectionKey];
    if (activeRequest) {
      if (!activeRequest.signal.aborted) {
        activeRequest.abort();
      }
    }
    const fetchController = (controller) => { this.activeRequests[sectionKey] = controller; };

    const fetchOptions = {
      activeSection,
      fetchController,
      isCollection: BFG.resource.type === 'collection',
      resourceKey: BFG.resource.key,
      resourceType: BFG.resource.type,
      sectionKey,
      showSections,
    };

    return getResourceAssets(params, fetchOptions);
  }

  updateSearchFilters = (updatedSearchFilters) => {
    this.setState({ searchFilters: updatedSearchFilters });
  }

  initialSearchOperator = () => {
    const searchOperator = this.urlParams().o; // string or undefined
    if (searchOperator) {
      return SearchOperatorOptions[searchOperator]; // string or undefined
    }
    return undefined;
  }

  initialSearchFilterOperators = () => {
    const searchOperator = this.initialSearchOperator();
    if (searchOperator) {
      return {
        ...defaultSearchFilterOperators,
        tags: searchOperator
      };
    }
    return defaultSearchFilterOperators;
  }

  initialSearchQuery = () => {
    let searchQuery = this.urlParams().q;
    if (searchQuery?.substr(0, 9) !== "section:") {
      try {
        searchQuery = decodeURIComponent(searchQuery?.replace(/[+]/g, ' ') || '');
      } catch (error) {
        console.log(error);
      }
      return searchQuery.replace(': "', ':"');
    }
    return undefined;
  }

  urlParams() {
    /**
     * {
     *  o: "foo"
     *  q: "bar"
     * }
     */
    const params = {};
    window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, (m, key, value) => params[key] = value); // eslint-disable-line no-return-assign
    return params;
  }

  showFilterModal(currentQuery) {
    const query = encodeURIComponent(currentQuery || this.state.searchQuery);
    const title = t`Create New Pinned Search`;
    const base_url = "/api/search_filters/new";
    const token_param = `?token=${BF_Token}`;
    const query_param = `&query=${query}`;
    const resource_params = `&resource_key=${this.resourceKey}&resource_type=${this.resourceType}`;
    const url = base_url + token_param + query_param + resource_params;

    BF.dialog.render(title, url, () => {
      // TODO: implement this modal with react
      $('#dialog').find('.modal-dialog').addClass('modal-sweet-alert create-pin-modal');
      $('.j-search-filter-submit').on('click', () => {
        addFilter({
          resourceKey: this.resourceKey,
          resourceType: this.resourceType,
          label: sanitize($('#search_filter_label').val()),
          query: sanitize($('#search_filter_query').val()),
        }).then((response) => {
          BF.dialog.dismiss();
          this.setState({ searchFilters: response });
        }).catch((response) => {
          if (response.responseJSON.label) {
            $('#search_filter_label').addClass('invalid');
          }

          if (response.responseJSON.query) {
            $('#search_filter_query').addClass('invalid');
          }
        });
      });
      $('#search_filter_query').on('keydown', () => {
        $(this).removeClass('invalid');
      });
      $('#search_filter_label').on('keydown', () => {
        $(this).removeClass('invalid');
      });
    });
  }

  allAssetKeys() {
    const sectionAssets = Object.values(this.state.assets);

    if (!sectionAssets.length) { return []; }

    return sectionAssets.reduce((acc, val) => acc.concat(val), []).map((asset) => asset?.id);
  }

  allVisibleAssetKeys() {
    const { activeSection } = this.state;
    let assets = { ...this.state.assets };

    if (activeSection !== 'all') {
      assets = { [activeSection]: assets[activeSection] || [] };
    }

    const sectionAssets = Object.values(assets);
    if (!sectionAssets.length) { return []; }

    return sectionAssets.reduce((acc, val) => acc.concat(val), []).map((asset) => asset?.id);
  }

  allViewOnlyAssetKeys() {
    const { downloadRequestId } = this.props;

    const viewOnlyAssetsEnabled = BFG.hasFeature('view_only_assets');
    if (!viewOnlyAssetsEnabled || downloadRequestId) { return []; }

    const { assets } = this.state;

    const sectionAssets = Object.values(assets);
    if (!sectionAssets.length) { return []; }

    const reduced = sectionAssets.reduce((acc, val) => acc.concat(val), []);
    const filtered = reduced.filter((asset) => asset?.attributes?.view_only);

    return filtered.map((asset) => asset?.id);
  }

  allVisibleViewOnlyAssetKeys() {
    const { downloadRequestId } = this.props;

    const viewOnlyAssetsEnabled = BFG.hasFeature('view_only_assets');
    if (!viewOnlyAssetsEnabled || downloadRequestId) { return []; }

    const { activeSection } = this.state;
    let assets = { ...this.state.assets };

    if (activeSection !== 'all') {
      assets = { [activeSection]: assets[activeSection] || [] };
    }

    const sectionAssets = Object.values(assets);
    if (!sectionAssets.length) { return []; }

    const reduced = sectionAssets.reduce((acc, val) => acc.concat(val), []);
    const filtered = reduced.filter((asset) => asset?.attributes?.view_only);

    return filtered.map((asset) => asset?.id);
  }

  escapedSearchQuery(searchQuery, searchOperator) {
    return (searchQuery && (searchOperator === SearchOperatorOptions.Quoted || searchOperator === SearchOperatorOptions.Strict))
      ? `"${searchQuery.replace(/"/g, '\\"')}"`
      : searchQuery;
  }

  sectionShouldRender(sectionKey) {
    const { sections, editable } = this.props;
    const {
      assets,
      loading,
      searchQuery,
      filterStrings,
      activeSection: activeSectionKey,
      userViewOptions,
    } = this.state;

    const sectionData = this.getSection(sectionKey);
    if (sectionData?.deferred) return true;

    const activeSection = sections.find((section) => section.section_key.toString() === activeSectionKey);
    const isActive = activeSection ? activeSection.section_key === sectionKey : false;
    const hasAssets = Boolean(assets[sectionKey]) && assets[sectionKey].length > 0;
    const hideWhenEmpty = userViewOptions.showEmptySections === false && !hasAssets;
    const noActiveNoSearch = !activeSection && !searchQuery && !filterStrings.length;

    return !userViewOptions?.showSections // <-- always show section pills as clickable when showSections === false
      || (editable && !hideWhenEmpty && (noActiveNoSearch || loading || hasAssets || isActive))
      || (!editable && hasAssets);
  }

  renderContent() {
    const {
      editable,
      printuiSectionId,
      sectionsToShow,
      authenticityToken,
      lazyLoadCards,
      libraryName,
      enableContactSheets,
      isPreview,
      downloadRequestId
    } = this.props;
    let { sections } = this.props;

    const {
      activeSection,
      assets,
      canvaApi,
      customFieldKeys,
      dependentCustomFields,
      entireSectionSelected,
      internalShareLinkExpired,
      filters,
      loading: allLoading,
      searchFilterOperators,
      searchQuery,
      selectedAssetKeys,
      selectedViewOnlyAssetKeys,
      tasks,
      userViewOptions,
      windowDimensions
    } = this.state;

    const isTest = window.BF_Environment === 'test';
    const search = this.escapedSearchQuery(searchQuery, userViewOptions.searchOperator);

    if (internalShareLinkExpired) {
      return (
        <StandardAlert heading={<Trans>This shareable link has expired.</Trans>} />
      )
    }

    const returnSection = ({
      currentPage,
      deferred,
      index,
      loading,
      section,
      sectionAssets,
      sectionTasks,
      totalPages,
      total,
    }) => (
      <Section
        key={section.section_key}
        activeSection={activeSection}
        allAssetsSelected={entireSectionSelected.has(section.section_key)}
        assets={sectionAssets}
        authenticityToken={authenticityToken}
        canvaApi={canvaApi}
        currentPage={currentPage}
        customFieldKeys={customFieldKeys}
        deferred={deferred}
        dependentCustomFields={dependentCustomFields}
        downloadRequestId={downloadRequestId}
        editable={editable}
        enableContactSheets={enableContactSheets}
        isPreview={isPreview}
        isRendered={isTest ? true : index === 0}
        lazyLoadCards={lazyLoadCards}
        libraryName={libraryName}
        loading={allLoading || Boolean(loading)}
        onAssetProcessing={(quantity) => this.setState({ assetsProcessing: quantity })}
        onNewAssets={(assetData) => this.onNewAssets(section.section_key, assetData)}
        onUpdate={(options) => this.fetchSection(section.section_key, options)}
        printuiSectionId={printuiSectionId}
        searchQuery={filterLogic(filters, search, searchFilterOperators)}
        section={section}
        selectedAssetKeys={selectedAssetKeys}
        selectedViewOnlyAssetKeys={selectedViewOnlyAssetKeys}
        shiftSelect={this.shiftSelect}
        tasks={sectionTasks}
        toggleSelected={this.toggleSelected}
        totalAssets={total}
        totalPages={totalPages || 1}
        userViewOptions={userViewOptions}
        windowDimensions={windowDimensions}
      />
    );

    const activeAssets = assets?.[activeSection]
      || assets?.['search-results-assets']
      || assets?.['show-page-assets'];

    const showNoResults = (searchQuery?.length > 0 && this.allAssetKeys().length === 0)
      || (activeAssets?.length === 0 && !userViewOptions?.showSections && activeSection === 'all');

    if (!allLoading && showNoResults) {
      if (isAiSearchOn()) {
        return <SemanticNoResults />;
      }
      return (
        <div>
          <p className="no-results-copy" tabIndex={-1}>
            <Trans>No results found</Trans>
          </p>
        </div>
      );
    }

    if (!userViewOptions?.showSections && activeSection === 'all') {
      // when sectionless and the 'All' section pill is selected
      const name = searchQuery ? t`Search Results` : t`All Assets`;
      const section_key = searchQuery ? 'search-results-assets' : 'show-page-assets';

      const allSectionAssets = assets[section_key] || [];
      const sectionlessAssets = allSectionAssets;

      return returnSection({
        index: 0,
        section: { name, section_key, default_asset_type: undefined, id: 0 },
        sectionAssets: sectionlessAssets,
        sectionTasks: tasks[section_key] || [],
        ...this.getSection(section_key),
      });
    }

    if (!userViewOptions?.showSections) {
      // when sectionless and a section pill is selected
      sections = sections.filter((section) => section.section_key === activeSection);
    }

    return sections.map((section, index) => {
      if (!editable && !sectionsToShow.includes(section.id)) return undefined;
      if (!this.sectionShouldRender(section.section_key)) return undefined;

      const allAssets = assets[section.section_key] || [];
      const sectionAssets = allAssets;

      return returnSection({
        index,
        section,
        sectionAssets,
        sectionTasks: tasks[section.section_key] || [],
        ...this.getSection(section.section_key),
      });
    });
  }

  render() {
    const {
      approver,
      defaultSortOrder,
      editable,
      libraryName,
      paginatePer,
      parentBrandfolders,
      pinsDropdownSetting,
      requestDownloadName,
      requestDownloadRequestee,
      sections,
      sectionDropdownSetting: sectionDropdownSettingProp,
      sectionsToShow,
      showTags
    } = this.props;

    const {
      activeLabelKey,
      activeSection,
      activeSearchFilter,
      downloadAlertCallback,
      filters,
      filtersDrawerOpen,
      filterStrings,
      assetsProcessing,
      labels,
      lastVisited,
      loading,
      searchableThings,
      searchKey,
      searchFilterOperators,
      searchFilters,
      searchQuery,
      searchSubmitted,
      selectedAssetKeys,
      selectedViewOnlyAssetKeys,
      showAdvancedDownloadModal,
      showDownloadAlert,
      showFullQuery,
      showWebToPrintModal,
      userViewOptions,
      windowDimensions,
      aiSearchEnabled,
      searchPageMode,
      nlsScores,
    } = this.state;

    const sectionDropdownSetting = searchPageMode ? false : sectionDropdownSettingProp;

    const { searchOperator, selectedUGTLocale, showAsGrid, showSections, sortOption } = userViewOptions;
    const listView = !showAsGrid;
    const filtersDrawerClass = filtersDrawerOpen !== "none" ? "filters-open" : "";
    const labelsFeatureClass = BFG.context.hasFeature("labels") && labels?.labelsResponse?.length
      ? "labels-feature-enabled"
      : '';

    const disabledSections = sections.filter((section) => (
      !this.sectionShouldRender(section.section_key)
    )).map((section) => section.section_key);

    let activeLabelName = null;
    if (labels.labelsFlat) {
      const label = labels.labelsFlat[activeLabelKey];
      activeLabelName = label ? label.name : null;
    }

    return (
      <SectionsContext.Provider
        value={{
          activeLabelKey,
          activeLabelName,
          addRemoveSelected: this.addRemoveSelected,
          editable,
          filtersDrawerOpen,
          labelsDrawerOpen: filtersDrawerOpen === 'labels',
          listView,
          parentBrandfolders,
          nlsScores,
          searchQuery,
          selectedAssetKeys,
          selectedViewOnlyAssetKeys,
          shiftSelect: this.shiftSelect,
          showSections,
          showTags,
          sortOption,
          submitSearchQuery: this.submitSearchQuery,
          toggleSelected: this.toggleSelected,
          windowDimensions,
        }}
      >
        <CustomDragLayer selectedAssetKeys={selectedAssetKeys} />
        <div className="custom_section_title" />
        {!BFG.showPageLite && !searchPageMode && (
          <SectionSort
            activeSection={activeSection}
            disabledSections={disabledSections}
            editable={editable}
            manageSectionsFeature={BFG.context.hasFeature("manage_sections")}
            sectionDropdownSetting={sectionDropdownSetting}
            sections={sections}
            sectionsToShow={sectionsToShow}
            showSections={showSections}
            toggleActiveSection={this.toggleActiveSection}
          />
        )}
        {(!BFG.showPageLite && !searchPageMode && (editable || searchFilters.length > 0)) && (
          <PinnedSearches
            activeSearchFilter={activeSearchFilter}
            editable={editable}
            hideCreateButton={searchPageMode}
            pinsDropdownSetting={pinsDropdownSetting}
            searchFilters={searchFilters}
            searchQuery={searchQuery}
            searchSubmitted={searchSubmitted}
            submitSearchQuery={this.submitSearchQuery}
            updateSearchFilters={this.updateSearchFilters}
          />
        )}
        <div className={`brandfolder-assets-wrapper ${filtersDrawerClass} ${labelsFeatureClass}`} data-cx-customization={`brandfolder-assets-wrapper`}>
          {!BFG.showPageLite && (
            <FiltersDrawer
              activateLabel={this.activateLabel}
              activeLabelKey={activeLabelKey}
              filters={filters}
              filtersDrawerOpen={filtersDrawerOpen}
              hideLabels={searchPageMode}
              labels={labels}
              loading={loading}
              searchFilterOperators={searchFilterOperators}
              searchableThings={searchableThings}
              sections={sections}
              selectedAssetKeys={selectedAssetKeys}
              selectedUGTLocale={selectedUGTLocale || BFG.locales.ugtLocaleDefault}
              showSectionsFilter={searchPageMode}
              toggleFiltersDrawer={this.toggleFiltersDrawer}
              updateFilters={this.updateFilters}
              updateSearchFilterOperator={this.updateSearchFilterOperator}
            />
          )}
          <div className="asset-content">
            {BFG.downloadRequestKey && (
              <ApproveRequestActions
                selectAll={this.selectAll}
              />
            )}
            {!BFG.showPageLite && !searchPageMode && activeLabelKey && BFG.context.hasFeature("labels") ? (
              <LabelsBreadcrumbs
                activateLabel={this.activateLabel}
                activeLabelKey={activeLabelKey}
                labelsFlat={labels.labelsFlat}
              />
            ) : ''}
            {!BFG.showPageLite && (
              // Note that the key here is important! Changing it causes the component to refresh,
              // which is how it receives new props, specifically the searchQuery when saved
              // searches are clicked. It's a little magical, but it works great!
              <>
                <SectionSearch
                  key={searchKey}
                  activeSectionKeys={searchPageMode ? (filters.sections || []).map((s) => s.key) : (activeSection && activeSection !== 'all' ? [activeSection] : [])}
                  addRemovePinnedSearch={this.addRemovePinnedSearch} // TODO: don't rely on this
                  brandfolderKey={BF.fx.brandfolderID()}
                  editable={editable}
                  hideSearchFilterPins={searchPageMode || isAiSearchOn()}
                  hideSearchOperator={searchPageMode || isAiSearchOn()}
                  loading={loading}
                  searchFilters={searchFilters}
                  searchPageMode={searchPageMode}
                  searchQuery={searchQuery}
                  searchSubmitted={searchSubmitted}
                  submitSearchQuery={this.submitSearchQuery}
                  toggleFiltersDrawer={this.toggleFiltersDrawer}
                  updateUserViewOptions={this.updateUserViewOptions}
                  userViewOptions={userViewOptions}
                  windowDimensions={windowDimensions}
                />
                <div className="search-and-view-details">
                  <SearchDetails
                    filterStrings={filterStrings}
                    filters={filters}
                    resetFilters={this.resetFilters}
                    searchFilterOperators={searchFilterOperators}
                    searchOperator={searchOperator}
                    searchQuery={searchQuery}
                    showFullQuery={showFullQuery}
                    toggleShowFullQuery={() => this.setState((prevState) => ({ showFullQuery: !prevState.showFullQuery }))}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <ViewOptions
                      defaultSortOrder={defaultSortOrder}
                      hideOrganizeBySections={searchPageMode}
                      paginatePer={paginatePer}
                      searchSubmitted={searchSubmitted}
                      updateUserViewOptions={this.updateUserViewOptions}
                      userViewOptions={userViewOptions}
                    />
                  </div>
                </div>
              </>
            )}
            <InternalShareBar allAssetKeys={new Set(this.allAssetKeys())} />
            <div
              aria-atomic="true"
              aria-live="polite"
              className="sr-only"
              role="status"
            >
              {this.state.searchResultsAnnouncement}
            </div>
            <div
              ref={this.assetContentRef}
              aria-busy={loading}
              aria-label={searchQuery ? t`Search results` : t`Assets`}
              className="section-results-container"
              id="asset-search-results"
              role="region"
              tabIndex={-1}
            >
              {this.renderContent()}
            </div>
            <BulkSelectBar
              activeLabelKey={activeLabelKey}
              addRemoveSelected={this.addRemoveSelected}
              allVisibleAssetKeys={this.allVisibleAssetKeys()}
              approver={approver}
              assetsProcessing={assetsProcessing}
              clearSelected={this.clearSelected}
              editable={editable}
              labels={labels}
              libraryName={libraryName}
              requestDownloadName={requestDownloadName}
              requestDownloadRequestee={requestDownloadRequestee}
              selectAllVisible={this.selectAllVisible}
              selectedAssetKeys={selectedAssetKeys}
              selectedViewOnlyAssetKeys={selectedViewOnlyAssetKeys}
              ugtLocale={userViewOptions.selectedUGTLocale || ''}
              userNotificationsPath={this.props.userNotificationsPath}
              windowDimensions={windowDimensions}
            />
            {BFG.context.hasFeature("web_to_print") && showWebToPrintModal && (
              <FetchControllersWrapper>
                <Web2PrintModal
                  assetKey={lastVisited.assetKey}
                  collectionId={Collection.key || null}
                  collectionSlug={Collection.slug || null}
                  launchedFromModal
                  resetShowModal={() => this.setState({ showWebToPrintModal: false })}
                />
              </FetchControllersWrapper>
            )}
            {BFG.context.hasFeature("cropping") && showAdvancedDownloadModal && (
              <AdvancedDownloadModal
                attachmentKey={lastVisited.attachmentKey}
                editable={editable}
                goBackToAsset={lastVisited.assetKey}
                resetShowModal={() => this.setState({ showAdvancedDownloadModal: false })}
                resource={{ type: this.resourceType.toLowerCase(), key: this.resourceKey }}
                shouldCloseOnOverlayClick={false}
              />
            )}
            <DownloadAlertDialog
              downloadAlert={this.props.downloadAlert}
              downloadCallback={downloadAlertCallback}
              open={showDownloadAlert}
              setOpen={(open) => this.setState({ showDownloadAlert: open })}
            />
          </div>
        </div>
        <div className={classnames("processing-loader-container",
          {
            active: assetsProcessing > 0,
            "bulk-select-bar-active": this.state.selectedAssetKeys.size > 0,
          })}
        >
          {assetsProcessing > 0 && (
            <ProcessingLoader
              assetQuantity={this.state.assetsProcessing}
              onClose={() => this.setState({ assetsProcessing: 0 })}
            />
          )}
        </div>
      </SectionsContext.Provider>
    );
  }
}

ShowPage.propTypes = {
  approver: PropTypes.bool,
  controlledCustomFieldsEnabled: PropTypes.bool,
  downloadAlert: PropTypes.string,
  editable: PropTypes.bool,
  libraryName: PropTypes.string,
  pinsDropdownSetting: PropTypes.bool,
  searchPageMode: PropTypes.bool,
  sectionDropdownSetting: PropTypes.bool,
  defaultSortOrder: PropTypes.string,
  isPreview: PropTypes.bool,
  requestDownloadName: PropTypes.string,
  requestDownloadRequestee: PropTypes.string,
  searchFilters: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
  })),
  sections: PropTypes.arrayOf(PropTypes.shape({
    section_key: PropTypes.string,
  })),
  sectionsToShow: PropTypes.arrayOf(PropTypes.number),
  downloadRequestId: PropTypes.number,
  paginatePer: PropTypes.number.isRequired,
  printuiSectionId: PropTypes.number,
  lazyLoadCards: PropTypes.bool,
  enableContactSheets: PropTypes.bool,
  showTags: PropTypes.bool,
};

ShowPage.defaultProps = {
  approver: false,
  controlledCustomFieldsEnabled: false,
  defaultSortOrder: 'position ASC',
  downloadAlert: '',
  downloadRequestId: null,
  editable: false,
  enableContactSheets: false,
  isPreview: false,
  lazyLoadCards: true,
  libraryName: "Brandfolder",
  pinsDropdownSetting: false,
  printuiSectionId: null,
  requestDownloadName: '',
  requestDownloadRequestee: '',
  searchFilters: [],
  searchPageMode: false,
  sectionDropdownSetting: false,
  sections: [],
  sectionsToShow: [],
  showTags: true,
};

export default ShowPage;
